import { useEffect } from "react";

interface SEOProps {
  title: string;
  description: string;
  canonical: string;
  ogImage?: string;
  type?: string;
  keywords?: string;
}

const APEX_ORIGIN = "https://bharatbulksms.com";
const normalizeOrigin = (value: string) => value.replace("https://bharatbulksms.com", APEX_ORIGIN);
const BUSINESS_ADDRESS = {
  "@type": "PostalAddress",
  streetAddress: "Ostwal Shopping Centre, Opposite Bhayandar East Station, Jesal Park",
  addressLocality: "Bhayandar East",
  addressRegion: "Maharashtra",
  postalCode: "401105",
  addressCountry: "IN",
};

const serviceSeoByPath: Record<string, Pick<SEOProps, "title" | "description" | "keywords">> = {
  "/bulk-sms": { title: "Bulk SMS Service in India | Promotional & Transactional Messaging", description: "Bulk SMS service in India for promotional, transactional and OTP campaigns, with DLT guidance, reporting and API options for business messaging.", keywords: "bulk sms service india, bulk sms service provider india, promotional bulk sms, transactional sms service, bulk sms api india" },
  "/transactional-sms": { title: "Transactional SMS Service India | Operational Alerts", description: "Plan transactional SMS for order updates, account activity, payment notifications and appointment reminders with DLT-related guidance.", keywords: "transactional sms service india, order update sms, payment alert sms, transactional sms api" },
  "/promotional-sms": { title: "Promotional SMS Service India | Campaign Planning", description: "Plan promotional SMS for launches, offers, events and customer engagement with audience, scheduling and DLT workflow guidance.", keywords: "promotional sms service india, sms campaign planning, promotional bulk sms, sms marketing campaigns" },
  "/otp-sms": { title: "OTP SMS Service India | Authentication Messaging", description: "Plan OTP SMS for sign-in, verification and confirmation flows, including code lifecycle, template and API implementation considerations.", keywords: "otp sms service india, otp sms api india, one time password sms, two factor authentication SMS" },
  "/whatsapp-business-api": { title: "WhatsApp Business API India | Template Messaging", description: "Explore WhatsApp Business messaging for approved templates, rich media, catalog messages and customer communication workflows.", keywords: "whatsapp business api india, whatsapp template messaging, whatsapp business integration, business whatsapp workflow" },
  "/voice-call": { title: "Voice Call Service India | Outbound Call Workflows", description: "Explore outbound voice-call workflows for alerts, reminders, broadcasts and multilingual customer communication planning.", keywords: "voice call service india, outbound voice calls, automated call workflow, voice broadcast service" },
  "/google-ads-services": { title: "Google Ads Management India | Search & Display Planning", description: "Plan Google Ads campaigns across Search, Display, Shopping and YouTube formats with measurement and landing-page considerations.", keywords: "google ads management india, search ads planning, display advertising india, google ads campaign management" },
  "/seo-services": { title: "SEO Services India | Technical & Content Planning", description: "Explore SEO planning for keyword research, on-page improvements, technical checks, content architecture and organic-search visibility.", keywords: "seo services india, technical seo india, on page seo services, content seo planning" },
  "/rcs-messaging": { title: "RCS Business Messaging India | Interactive Formats", description: "Explore RCS messaging formats, branded profiles, interactive cards and implementation considerations for business communication.", keywords: "rcs messaging india, rcs business messaging, interactive business messaging, rcs api india" },
  "/email-marketing": { title: "Email Marketing Services India | Campaign Workflows", description: "Plan email newsletters, lifecycle sequences, audience segmentation and campaign measurement for business communication.", keywords: "email marketing services india, email campaign planning, newsletter workflow, email automation strategy" },
  "/social-media-marketing": { title: "Social Media Marketing India | Content & Community Workflows", description: "Plan social publishing, community communication, content calendars and channel-specific workflows for business brands.", keywords: "social media marketing india, social media content planning, social media management, community workflow" },
  "/website-design": { title: "Website Development Company India | Business Websites", description: "Website development for Indian businesses, from responsive company websites and landing pages to e-commerce planning and SEO-ready structure. Share your requirements with Bharat Bulk SMS.", keywords: "website development company india, website design india, business website development, responsive website development" },
  "/meta-ads": { title: "Meta Ads India | Facebook & Instagram Campaign Planning", description: "Explore Meta advertising workflows for Facebook and Instagram, including audience planning, creative preparation and reporting.", keywords: "meta ads india, facebook ads planning, instagram ads campaign, meta advertising workflow" },
  "/lead-generation": { title: "Lead Generation Services India | Qualification Workflows", description: "Plan lead-generation workflows across paid media, messaging and landing pages, with qualification and follow-up considerations.", keywords: "lead generation services india, lead qualification workflow, b2b lead generation, digital lead generation" },
  "/content-marketing": { title: "Content Marketing Services India | Editorial Planning", description: "Plan editorial content, search-focused topic clusters and multi-format publishing workflows for business communication.", keywords: "content marketing services india, editorial content planning, seo content strategy, business content workflow" },
  "/local-seo": { title: "Local SEO Services India | Local Discovery Planning", description: "Plan local-search information, location pages, business details and local discovery improvements for relevant customer searches.", keywords: "local seo services india, local search planning, business location seo, local discovery workflow" },
  "/dlt-registration": { title: "DLT Registration India | SMS Compliance Guidance", description: "Understand DLT registration, entity records, sender headers, template submission and SMS compliance workflow requirements.", keywords: "dlt registration india, sms dlt compliance, sender header registration, sms template registration" },
  "/social-media-monetization": { title: "Social Media Monetization India | Creator Business Planning", description: "Explore creator-business planning, audience communication and monetization workflow considerations across social platforms.", keywords: "social media monetization india, creator business planning, creator audience workflow, social content monetization" },
  "/services/toll-free-number": { title: "Toll-Free Number India | Customer Contact Workflows", description: "Explore toll-free number planning for inbound customer contact, call routing, support workflows and business communication.", keywords: "toll free number india, business toll free number, inbound call workflow, customer contact number" },
  "/services/ivr-solutions": { title: "IVR Solutions India | Inbound Call Routing", description: "Plan IVR menus, inbound call routing, caller options and business phone workflows for customer communication.", keywords: "ivr solutions india, inbound call routing, interactive voice response, ivr menu workflow" },
  "/services/missed-call-services": { title: "Missed Call Service India | Callback & Opt-in Workflows", description: "Explore missed-call workflows for opt-ins, callbacks, contact capture and customer response coordination.", keywords: "missed call service india, missed call opt in, callback workflow, missed call lead capture" },
  "/services/youtube-ads": { title: "YouTube Ads India | Video Campaign Planning", description: "Plan YouTube video advertising with audience selection, creative formats, campaign structure and measurement considerations.", keywords: "youtube ads india, youtube video advertising, video campaign planning, youtube advertising service" },
  "/reseller-program": { title: "Bulk SMS Reseller Program India | White-Label Workflow", description: "Explore white-label SMS reseller workflows, account access, customer management and operational implementation requirements.", keywords: "bulk sms reseller program india, white label sms workflow, sms reseller panel, messaging reseller business" },
  "/international-bulk-sms": { title: "International Bulk SMS Service | Cross-Border Messaging", description: "Plan international SMS communication with country, recipient consent, content, route and implementation considerations.", keywords: "international bulk sms, cross border sms messaging, international sms planning, global sms workflow" },
};

export function useSEO({
  title,
  description,
  canonical,
  ogImage = "https://bharatbulksms.com/logo.png",
  type = "website",
  keywords,
}: SEOProps) {
  useEffect(() => {
    const pathname = window.location.pathname.replace(/\/$/, "") || "/";
    const serviceSeo = serviceSeoByPath[pathname];
    const effectiveTitle = serviceSeo?.title ?? title;
    const effectiveDescription = serviceSeo?.description ?? description;
    const effectiveKeywords = serviceSeo?.keywords ?? keywords;
    const normalizedCanonical = normalizeOrigin(canonical);
    const normalizedOgImage = normalizeOrigin(ogImage);
    document.title = effectiveTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    setMeta("description", effectiveDescription);
    setMeta("robots", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
    if (effectiveKeywords) setMeta("keywords", effectiveKeywords);

    setMeta("og:title", effectiveTitle, true);
    setMeta("og:description", effectiveDescription, true);
    setMeta("og:url", normalizedCanonical, true);
    setMeta("og:image", normalizedOgImage, true);
    setMeta("og:type", type, true);
    setMeta("og:site_name", "Bharat Bulk SMS", true);
    setMeta("og:locale", "en_IN", true);

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", effectiveTitle);
    setMeta("twitter:description", effectiveDescription);
    setMeta("twitter:image", normalizedOgImage);

    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = normalizedCanonical;

    document.querySelectorAll('link[rel="canonical"]').forEach((link, index) => {
      if (index > 0) link.remove();
    });
    document.querySelectorAll('script[type="application/ld+json"]').forEach((script) => {
      if (script.textContent?.includes("https://bharatbulksms.com")) {
        script.textContent = script.textContent.replaceAll("https://bharatbulksms.com", APEX_ORIGIN);
      }
      try {
        const structuredData = JSON.parse(script.textContent || "{}");
        if (
          serviceSeo &&
          (structuredData["@type"] === "FAQPage" || structuredData["@type"] === "Service")
        ) {
          script.remove();
          return;
        }
        script.textContent = JSON.stringify(structuredData);
      } catch {
        // Leave malformed or non-JSON scripts untouched rather than disrupting the page.
      }
    });
  }, [title, description, canonical, ogImage, type, keywords]);
}

export function StructuredData() {
  const address = BUSINESS_ADDRESS;

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Bharat Bulk SMS",
    alternateName: "Bharat Bulk SMS",
    url: APEX_ORIGIN,
    description: "Business messaging and digital marketing service information for organisations in India.",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+91-7208417941",
      contactType: "customer support",
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
    },
    address,
    knowsAbout: ["Bulk SMS", "WhatsApp Business API", "Voice and IVR", "Digital marketing"],
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Bharat Bulk SMS",
    alternateName: "Bharat Bulk SMS",
    url: APEX_ORIGIN,
    description: "Business messaging and digital marketing service information for organisations in India.",
    inLanguage: "en-IN",
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${APEX_ORIGIN}/#localbusiness`,
    name: "Bharat Bulk SMS",
    telephone: "+91-7208417941",
    email: "support@bharatbulksms.com",
    url: APEX_ORIGIN,
    description: "Business messaging and digital marketing service information.",
    address,
    areaServed: "IN",
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
    </>
  );
}
